import React, { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { fabric } from 'fabric';
import AccordionToolbar from './AccordionToolbar';
import PropertiesPanel from './PropertiesPanel';
import DatabaseManager from '../../components/DatabaseManager'; // Importar DatabaseManager
import { getApiBaseUrl } from '../config/api';
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';
import CodeGeneratorModal from './CodeGeneratorModal';
import NanoTextModal from './modals/NanoTextModal';
import MicroTextModal from './modals/MicroTextModal';
import UVLayerModal from './modals/UVLayerModal';
import GhostImageModal from './modals/GhostImageModal';
import HologramModal from './modals/HologramModal';
import { DesignStorageService } from '../utils/DesignStorageService';
import DesignBrowserModal from './modals/DesignBrowserModal.jsx';
import SaveDesignModal from './modals/SaveDesignModal';
import CardConfigModal from './modals/CardConfigModal';
import TemplateModal from './modals/TemplateModal';
import ArtisticTextModal from './modals/ArtisticTextModal';
import CameraCaptureModal from './modals/CameraCaptureModal';
import SignatureCaptureModal from './modals/SignatureCaptureModal';
import FingerprintCaptureModal from './modals/FingerprintCaptureModal';
import ExportModal from './modals/ExportModal';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Hook de notificaciones inline
const useNotifications = () => {
    const showNotification = (message, type) => {
        console.log(`[${type.toUpperCase()}] ${message}`);
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 20px;
            background: ${type === 'success' ? '#4caf50' : type === 'error' ? '#f44336' : type === 'warning' ? '#ff9800' : '#2196f3'};
            color: white;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            z-index: 10000;
            font-family: system-ui, -apple-system, sans-serif;
            animation: slideIn 0.3s ease-out;
        `;
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => {
            notification.style.animation = 'slideOut 0.3s ease-in';
            setTimeout(() => notification.remove(), 280);
        }, 3000);
    };

    return {
        showSuccess: (msg) => showNotification(msg, 'success'),
        showError: (msg) => showNotification(msg, 'error'),
        showWarning: (msg) => showNotification(msg, 'warning'),
        showInfo: (msg) => showNotification(msg, 'info')
    };
};

const CardDesigner = forwardRef(({ user }, ref) => {
    // Estados principales
    const [currentSide, setCurrentSide] = useState('front');
    const [isDoubleSided, setIsDoubleSided] = useState(false);
    const [selectedObject, setSelectedObject] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [canvasReady, setCanvasReady] = useState(false);
    const [canvasDimensions, setCanvasDimensions] = useState({ width: 640, height: 400 });

    // Estados de Impresión
    const [printers, setPrinters] = useState([]);
    const [selectedPrinter, setSelectedPrinter] = useState('');
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    // Estado local para Database Manager
    const [showDatabaseManager, setShowDatabaseManager] = useState(false);
    const handleDatabaseOpen = () => setShowDatabaseManager(true);

    useImperativeHandle(ref, () => ({
        openDatabaseManager: handleDatabaseOpen,
        saveDesign: handleSaveDesign,
        openDesignBrowser: () => setShowOpenModal(true)
    }));
    const [previewBackImage, setPreviewBackImage] = useState(null);
    const [isPrinting, setIsPrinting] = useState(false);

    // Estados de Impresión por Lotes
    const [printMode, setPrintMode] = useState('current'); // 'current', 'all', 'range'
    const [printRangeStart, setPrintRangeStart] = useState(1);
    const [printRangeEnd, setPrintRangeEnd] = useState(1);

    // Estado para Modal de Códigos
    const [showCodeModal, setShowCodeModal] = useState(false);

    // Estados para Modales de Seguridad
    const [showNanoTextModal, setShowNanoTextModal] = useState(false);
    const [showMicroTextModal, setShowMicroTextModal] = useState(false);
    const [showUVModal, setShowUVModal] = useState(false);
    const [showGhostModal, setShowGhostModal] = useState(false);
    const [showHologramModal, setShowHologramModal] = useState(false);
    const [showCardConfigModal, setShowCardConfigModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showArtisticTextModal, setShowArtisticTextModal] = useState(false);
    const [showCameraModal, setShowCameraModal] = useState(false);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [showFingerprintModal, setShowFingerprintModal] = useState(false);
    const [showOpenModal, setShowOpenModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [currentDesignId, setCurrentDesignId] = useState(null);
    const [currentDesignName, setCurrentDesignName] = useState('');
    const [searchParams] = useSearchParams();

    // Estados de Data Binding
    const [datasets, setDatasets] = useState([]);
    const [selectedDataset, setSelectedDataset] = useState(null);
    const [datasetRecords, setDatasetRecords] = useState([]);
    const [currentRecordIndex, setCurrentRecordIndex] = useState(0);
    const [datasetFields, setDatasetFields] = useState([]);
    const [propertyUpdateTrigger, setPropertyUpdateTrigger] = useState(0);

    const navigate = useNavigate();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Función para marcar cambios no guardados
    const markAsUnsaved = useCallback(() => {
        if (!hasUnsavedChanges) setHasUnsavedChanges(true);
    }, [hasUnsavedChanges]);
    const [fieldConfigs, setFieldConfigs] = useState({}); // Configuración de tipos de campos

    // Cargar configuraciones de campos al iniciar y al cerrar DB Manager
    useEffect(() => {
        if (!showDatabaseManager) {
            try {
                const saved = localStorage.getItem('matic_dataset_field_configs');
                if (saved) setFieldConfigs(JSON.parse(saved));
            } catch (e) { console.error(e); }
        }
    }, [showDatabaseManager]);

    // Cargar diseño desde URL si existe
    useEffect(() => {
        const designId = searchParams.get('designId');
        if (designId && !currentDesignId) {
            const design = DesignStorageService.getDesignById(designId);
            if (design) {
                // Pequeño delay para asegurar que Fabric esté listo
                setTimeout(() => handleLoadDesign(design), 500);
            }
        }
    }, []);


    // Refs para elementos DOM
    const frontCanvasEl = useRef(null);
    const backCanvasEl = useRef(null);

    // Refs para instancias de Fabric
    const frontFabricRef = useRef(null);
    const backFabricRef = useRef(null);

    // Notificaciones
    const { showSuccess, showError, showWarning, showInfo } = useNotifications();

    // === ESTILOS FORZADOS PARA CORREGIR FABRIC.JS ===
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .canvas-container { 
                position: relative !important; 
                padding: 0 !important; 
                margin: 0 auto !important; 
                overflow: hidden !important;
                background: transparent !important;
            }
            .canvas-container canvas { 
                position: absolute !important; 
                top: 0 !important; 
                left: 0 !important; 
                border: none !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            .canvas-container canvas.lower-canvas {
                z-index: 1 !important;
            }
            .canvas-container canvas.upper-canvas {
                z-index: 2 !important;
                background: transparent !important;
                background-color: transparent !important;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);




    // === INICIALIZACIÓN DE CANVAS ===
    useEffect(() => {
        const initCanvas = (el, refInstance) => {
            if (!el) return null;
            if (refInstance.current) {
                refInstance.current.dispose();
            }

            const canvas = new fabric.Canvas(el, {
                width: 640,
                height: 400,
                backgroundColor: '#ffffff',
                preserveObjectStacking: true,
                selection: true
            });

            refInstance.current = canvas;
            return canvas;
        };

        if (frontCanvasEl.current) {
            initCanvas(frontCanvasEl.current, frontFabricRef);
            setCanvasReady(true);
        }

        return () => {
            if (frontFabricRef.current) {
                frontFabricRef.current.dispose();
                frontFabricRef.current = null;
            }
            if (backFabricRef.current) {
                backFabricRef.current.dispose();
                backFabricRef.current = null;
            }
        };
    }, []);

    // Manejo de Doble Cara
    useEffect(() => {
        if (isDoubleSided && backCanvasEl.current && !backFabricRef.current) {
            const canvas = new fabric.Canvas(backCanvasEl.current, {
                width: 640,
                height: 400,
                backgroundColor: '#ffffff',
                preserveObjectStacking: true
            });
            backFabricRef.current = canvas;
            bindEvents(canvas);
        }
    }, [isDoubleSided]);

    useEffect(() => {
        if (!isDoubleSided) {
            setCurrentSide('front');
        }
    }, [isDoubleSided]);

    // Fetch Impresoras
    useEffect(() => {
        const fetchPrinters = async () => {
            const tryFetch = async (baseUrl) => {
                console.log('Trying to fetch printers from:', baseUrl);
                try {
                    // 1. Fetch Registered
                    const regRes = await fetch(`${baseUrl}/api/printer/registered`);
                    const regData = await regRes.json();
                    const registered = (regData.Success && regData.Printers) ? regData.Printers : [];

                    // 2. Fetch Discovered
                    const discRes = await fetch(`${baseUrl}/api/printer/discover`);
                    const discData = await discRes.json();
                    const discovered = (discData.success && discData.printers) ? discData.printers : [];

                    return { success: true, registered, discovered };
                } catch (e) {
                    console.warn(`Failed to fetch from ${baseUrl}:`, e);
                    return { success: false, registered: [], discovered: [] };
                }
            };

            const defaultBase = getApiBaseUrl();
            const candidates = [
                defaultBase,
                'https://printerserver.maticamexico.com.mx'
            ];

            // Remove duplicates
            const uniqueCandidates = [...new Set(candidates)];

            let allPrinters = [];
            let success = false;

            for (const url of uniqueCandidates) {
                const result = await tryFetch(url);
                if (result.success) {
                    // Normalize Discovered
                    const normalizedDiscovered = result.discovered.map(p => ({
                        _id: p.ip_address,
                        Name: p.name || p.model || 'Unknown',
                        Model: p.model,
                        IpAddress: p.ip_address,
                        ...p
                    }));

                    // Merge
                    allPrinters = [...result.registered];
                    normalizedDiscovered.forEach(d => {
                        const exists = allPrinters.some(r => r.IpAddress === d.ip_address || r.SerialNumber === d.serial_number);
                        if (!exists) {
                            allPrinters.push(d);
                        }
                    });

                    if (allPrinters.length > 0) {
                        success = true;
                        console.log(`Successfully loaded ${allPrinters.length} printers from ${url}`);
                        break; // Stop if we found printers
                    }
                }
            }

            console.log('Final Printer List:', allPrinters);
            setPrinters(allPrinters);

            if (allPrinters.length > 0 && !selectedPrinter) {
                setSelectedPrinter(allPrinters[0]._id);
            } else if (allPrinters.length === 0) {
                console.warn('No printers found on any candidate URL.');
            }
        };
        fetchPrinters();
    }, []);
    const getActiveCanvas = useCallback(() => {
        return currentSide === 'front' ? frontFabricRef.current : backFabricRef.current;
    }, [currentSide, canvasReady]);

    // === DATA BINDING LOGIC ===

    // Función para actualizar/generar códigos (QR/Barcode)
    const handleUpdateCode = useCallback(async (obj, type, value, format = 'CODE128') => {
        if (!obj || !value) return;

        try {
            let dataUrl;
            if (type === 'qrCode') {
                dataUrl = await QRCode.toDataURL(value, { width: 200, margin: 1 });
            } else if (type === 'barcode1d') {
                const canvas = document.createElement('canvas');
                JsBarcode(canvas, value, {
                    format: format,
                    displayValue: true
                });
                dataUrl = canvas.toDataURL();
            }

            if (dataUrl) {
                obj.setSrc(dataUrl, () => {
                    // Forzar actualización visual
                    obj.set('dirty', true);
                    obj.canvas?.renderAll();
                });
            }
        } catch (error) {
            console.error('Error generating code:', error);
            showError('Error al generar el código visual');
        }
    }, [showError]);

    // Función para actualizar propiedades genéricas del objeto seleccionado
    const handleUpdateProperty = useCallback((key, value) => {
        const canvas = getActiveCanvas();
        const activeObj = canvas?.getActiveObject();

        if (activeObj) {
            activeObj.set(key, value);

            // Si es dataField, intentar aplicar valor inmediato del registro actual
            if (key === 'dataField' && datasetRecords.length > 0) {
                const record = datasetRecords[currentRecordIndex];
                const recordData = record.data || record;

                if (recordData && recordData[value] !== undefined) {
                    const newValue = String(recordData[value]);
                    if (activeObj.type === 'text' || activeObj.type === 'i-text' || activeObj.type === 'textbox') {
                        activeObj.set('text', newValue);
                    } else if (activeObj.type === 'image' && (activeObj.customType === 'qrCode' || activeObj.customType === 'barcode1d')) {
                        activeObj.set('codeValue', newValue);
                        handleUpdateCode(activeObj, activeObj.customType, newValue, activeObj.codeFormat);
                    }
                }
            }

            // Si es textPattern, aplicar inmediatamente
            if (key === 'textPattern' && datasetRecords.length > 0) {
                const record = datasetRecords[currentRecordIndex];
                const recordData = record.data || record;

                let newText = value;
                Object.keys(recordData).forEach(k => {
                    const regex = new RegExp(`{${k}}`, 'g');
                    newText = newText.replace(regex, String(recordData[k] || ''));
                });
                // Limpiar variables no encontradas visualmente para no mostrar {Campo} roto
                newText = newText.replace(/{[^}]+}/g, '');

                activeObj.set('text', newText);
            }

            activeObj.setCoords();
            canvas.requestRenderAll();
            setPropertyUpdateTrigger(prev => prev + 1);
        }
    }, [getActiveCanvas, datasetRecords, currentRecordIndex, handleUpdateCode]);

    // 1. Fetch Datasets
    // 1. Fetch Datasets
    const fetchDatasets = useCallback(async () => {
        const userId = localStorage.getItem('accountId') || user?._id || user?.accountId;
        if (!userId) return;
        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/data/datasets?user_id=${userId}`);
            const data = await response.json();
            if (data.success) {
                setDatasets(data.datasets);
            }
        } catch (error) {
            console.error('Error fetching datasets:', error);
        }
    }, [user]);

    useEffect(() => {
        fetchDatasets();
    }, [fetchDatasets]);

    // 2. Fetch Records cuando cambia el dataset seleccionado
    useEffect(() => {
        const fetchRecords = async () => {
            if (!selectedDataset) {
                setDatasetRecords([]);
                setDatasetFields([]);
                return;
            }
            try {
                const datasetObj = datasets.find(d => d.dataset_name === selectedDataset);
                if (!datasetObj) return;

                console.log('Selected Dataset Object:', datasetObj);

                // La API requiere user_id y dataset_name
                const userId = localStorage.getItem('accountId') || user?._id || user?.accountId;
                console.log(`Fetching records for user: ${userId}, dataset: ${datasetObj.dataset_name}`);

                const baseUrl = getApiBaseUrl();
                const response = await fetch(`${baseUrl}/api/data/records?user_id=${userId}&dataset_name=${encodeURIComponent(datasetObj.dataset_name)}`);
                const data = await response.json();

                console.log('Records loaded:', data);

                if (data.success && data.records && data.records.length > 0) {
                    setDatasetRecords(data.records);
                    showInfo(`Cargados ${data.records.length} registros`);

                    // Lógica robusta para extraer campos
                    const firstRecord = data.records[0];
                    let fields = [];

                    if (firstRecord.data && typeof firstRecord.data === 'object') {
                        fields = Object.keys(firstRecord.data);
                    } else {
                        fields = Object.keys(firstRecord).filter(key =>
                            !['_id', 'dataset_id', 'import_id', '__v', 'created_at', 'updated_at'].includes(key)
                        );
                    }

                    console.log('Extracted fields:', fields);
                    if (fields.length === 0) {
                        showWarning('El dataset no tiene campos visibles');
                    }
                    setDatasetFields(fields);
                    setCurrentRecordIndex(0);
                } else {
                    console.warn('No records found for dataset');
                    showWarning('No se encontraron registros en este dataset');
                    setDatasetFields([]);
                }
            } catch (error) {
                console.error('Error fetching records:', error);
                showError('Error al cargar registros del dataset');
            }
        };

        fetchRecords();
    }, [selectedDataset, datasets]);

    // 3. Aplicar datos al Canvas
    const applyDataToCanvas = useCallback((recordIndex) => {
        if (!datasetRecords[recordIndex]) return;

        const record = datasetRecords[recordIndex];
        // Asegurar que recordData es un objeto válido
        let recordData = record.data || record;
        if (typeof recordData === 'string') {
            try { recordData = JSON.parse(recordData); } catch (e) { console.error('Error parsing record data', e); }
        }

        // Obtener configuración del dataset actual
        const currentDatasetObj = datasets.find(d => d.dataset_name === selectedDataset);
        const currentDatasetId = currentDatasetObj?._id;
        // FIX: Ensure currentConfig is always an object to avoid crashes
        const currentConfig = (currentDatasetId && fieldConfigs && fieldConfigs[currentDatasetId]) || {};

        const updateCanvasObjects = (canvas) => {
            if (!canvas) return false;
            const objects = canvas.getObjects();
            let updatedCount = 0;

            objects.forEach(obj => {
                // Lógica para Text Pattern (Concatenación)
                if ((obj.type === 'text' || obj.type === 'i-text' || obj.type === 'textbox') && obj.textPattern) {
                    let newText = obj.textPattern;
                    // Reemplazar todas las variables {Campo}
                    Object.keys(recordData).forEach(key => {
                        const regex = new RegExp(`{${key}}`, 'g');
                        newText = newText.replace(regex, String(recordData[key] || ''));
                    });
                    // Limpiar variables no encontradas
                    newText = newText.replace(/{[^}]+}/g, '');

                    if (obj.text !== newText) {
                        obj.set('text', newText);
                        obj.set('dirty', true);
                        updatedCount++;
                    }
                }
                // Lógica Data Field
                else if (obj.dataField) {
                    let value = recordData[obj.dataField];

                    // Fallback: búsqueda insensible a mayúsculas
                    if (value === undefined) {
                        const key = Object.keys(recordData).find(k => k.toLowerCase() === obj.dataField.toLowerCase());
                        if (key) value = recordData[key];
                    }

                    if (value !== undefined) {
                        const fieldConfig = currentConfig[obj.dataField] || {};

                        // Procesar según tipo configurado
                        if (fieldConfig.type === 'image_path') {
                            let basePath = fieldConfig.basePath || '';
                            const suffix = fieldConfig.suffix || '';
                            basePath = basePath.replace(/['"]/g, '');
                            if (basePath && !basePath.endsWith('/') && !basePath.endsWith('\\')) {
                                if (basePath.includes('\\')) basePath += '\\';
                                else basePath += '/';
                            }
                            value = basePath + value + suffix;
                        } else if (fieldConfig.type === 'boolean') {
                            value = value ? 'Sí' : 'No';
                        }

                        if (obj.type === 'text' || obj.type === 'i-text' || obj.type === 'textbox') {
                            const newValue = String(value);
                            if (obj.text !== newValue) {
                                obj.set('text', newValue);
                                obj.set('dirty', true);
                                updatedCount++;
                            }
                        } else if (obj.type === 'image') {
                            if (obj.customType === 'qrCode' || obj.customType === 'barcode1d') {
                                obj.set('codeValue', String(value));
                                handleUpdateCode(obj, obj.customType, String(value), obj.codeFormat);
                                updatedCount++;
                            } else {
                                // IMAGEN DINÁMICA (Foto)
                                if (value) {
                                    let imageUrl = String(value);
                                    if (imageUrl.match(/^[a-zA-Z]:\\/)) {
                                        imageUrl = 'file:///' + imageUrl.replace(/\\/g, '/');
                                    }
                                    obj.setSrc(imageUrl, (newImg) => {
                                        canvas.renderAll();
                                    });
                                    updatedCount++;
                                }
                            }
                        }
                    }
                }
            });
            return updatedCount > 0;
        };

        const frontUpdated = updateCanvasObjects(frontFabricRef.current);
        if (frontFabricRef.current) frontFabricRef.current.renderAll();

        const backUpdated = updateCanvasObjects(backFabricRef.current);
        if (backFabricRef.current) backFabricRef.current.renderAll();

    }, [datasetRecords, handleUpdateCode, datasets, selectedDataset, fieldConfigs]);

    // Navegación de registros
    const handleNextRecord = () => {
        if (currentRecordIndex < datasetRecords.length - 1) {
            const newIndex = currentRecordIndex + 1;
            setCurrentRecordIndex(newIndex);
            applyDataToCanvas(newIndex);
        }
    };

    const handlePrevRecord = () => {
        if (currentRecordIndex > 0) {
            const newIndex = currentRecordIndex - 1;
            setCurrentRecordIndex(newIndex);
            applyDataToCanvas(newIndex);
        }
    };

    const bindEvents = (canvas) => {
        if (!canvas) return;
        canvas.on('selection:created', (e) => setSelectedObject(e.selected ? e.selected[0] : null));
        canvas.on('selection:updated', (e) => setSelectedObject(e.selected ? e.selected[0] : null));
        canvas.on('selection:cleared', () => setSelectedObject(null));

        // Detectar cambios
        canvas.on('object:added', markAsUnsaved);
        canvas.on('object:modified', markAsUnsaved);
        canvas.on('object:removed', markAsUnsaved);

        // Lógica para Textbox: Ajustar width en lugar de scaleX al escalar
        canvas.on('object:scaling', (e) => {
            const obj = e.target;
            if (obj.type === 'textbox') {
                // Ajustar ancho basado en la escala X
                const newWidth = obj.width * obj.scaleX;
                obj.set({
                    width: newWidth,
                    scaleX: 1,
                    scaleY: 1 // Mantener escala Y en 1 para no cambiar tamaño de fuente
                });
            }
        });
    };

    useEffect(() => {
        if (frontFabricRef.current) bindEvents(frontFabricRef.current);
        if (backFabricRef.current) bindEvents(backFabricRef.current);
    }, []);



    const addObject = useCallback((obj) => {
        const canvas = getActiveCanvas();
        if (canvas) {
            if (obj.left === 0 && obj.top === 0) {
                canvas.centerObject(obj);
            }
            canvas.add(obj);
            canvas.setActiveObject(obj);
            canvas.renderAll();
            showSuccess('Objeto añadido correctamente');
        } else {
            showError('No hay canvas activo');
        }
    }, [getActiveCanvas, showSuccess, showError]);

    // === EXPORTACIÓN AVANZADA ===
    const handleExportAdvanced = () => {
        setShowExportModal(true);
    };

    const executeExport = (format, dpi) => {
        const canvas = getActiveCanvas();
        if (!canvas) {
            showError('No hay diseño activo para exportar');
            return;
        }

        // Calcular multiplicador basado en DPI
        // Base: CR80 (85.6mm) a 300 DPI = ~1011px
        const baseWidth300 = 1011;
        let targetWidth = baseWidth300;

        if (dpi === 600) targetWidth = 2022;
        if (dpi === 1200) targetWidth = 4044;

        const multiplier = targetWidth / canvas.width;

        showInfo(`Generando imagen a ${dpi} DPI...`);

        // Deseleccionar todo para la foto
        const activeObj = canvas.getActiveObject();
        canvas.discardActiveObject();
        canvas.renderAll();

        try {
            const dataUrl = canvas.toDataURL({
                format: format,
                quality: 1,
                multiplier: multiplier
            });

            // Re-seleccionar lo que estaba seleccionado
            if (activeObj) {
                canvas.setActiveObject(activeObj);
                canvas.renderAll();
            }

            // Descargar
            const link = document.createElement('a');
            link.download = `design-${currentSide}-${dpi}dpi.${format === 'jpeg' ? 'jpg' : format}`;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            showSuccess('Imagen exportada correctamente');
        } catch (error) {
            console.error('Error exporting image:', error);
            showError('Error al exportar la imagen');
        }
    };

    // === HERRAMIENTAS ===
    const handleNewDocument = () => {
        if (confirm('¿Estás seguro de crear un nuevo documento? Se perderán los cambios no guardados.')) {
            frontFabricRef.current?.clear();
            frontFabricRef.current?.setBackgroundColor('#ffffff', frontFabricRef.current.renderAll.bind(frontFabricRef.current));
            backFabricRef.current?.clear();
            backFabricRef.current?.setBackgroundColor('#ffffff', backFabricRef.current.renderAll.bind(backFabricRef.current));
            showSuccess('Nuevo documento creado');
        }
    };

    const handleTemplateSelection = () => setShowTemplateModal(true);

    const handleSaveDesign = () => {
        if (!currentDesignName || currentDesignName === 'undefined') {
            setShowSaveModal(true);
        } else {
            executeSave(currentDesignName);
        }
    };

    const executeSave = async (designName) => {
        const canvas = getActiveCanvas();
        if (!canvas) return;

        // 1. Generar Thumbnail
        const thumbnail = frontFabricRef.current ? frontFabricRef.current.toDataURL({ format: 'png', multiplier: 0.5, quality: 0.8 }) : '';

        // 2. Serializar Canvas (Front y Back)
        const propertiesToInclude = ['id', 'dataField', 'textPattern', 'customType', 'codeType', 'codeFormat', 'codeValue', 'lockScalingX', 'lockScalingY', 'editable', 'selectable'];
        const frontJson = frontFabricRef.current ? frontFabricRef.current.toJSON(propertiesToInclude) : null;
        const backJson = backFabricRef.current ? backFabricRef.current.toJSON(propertiesToInclude) : null;

        // 3. Configuración de Base de Datos
        const dbConfig = {
            selectedDataset,
            fieldConfigs,
            // Guardamos referencia mínima
            datasetName: selectedDataset
        };

        // 4. Construir Objeto de Diseño
        const designData = {
            id: currentDesignId, // Si es null, el servicio generará uno nuevo
            accountId: localStorage.getItem('accountId') || 'MATICA-DEMO-ACCOUNT-ID',
            name: designName,
            description: 'Diseño creado en Matica Desktop Client',
            thumbnail: thumbnail,
            thumbnail_front: thumbnail,
            thumbnail_back: thumbnail, // TODO: Generar thumbnail real del reverso
            canvasData: {
                front: frontJson,
                back: backJson,
                isDoubleSided,
                currentSide
            },
            // Mapeo para compatibilidad con backend antiguo (si no se ha actualizado)
            front_canvas_json: JSON.stringify(frontJson),
            back_canvas_json: backJson ? JSON.stringify(backJson) : null,
            is_double_sided: isDoubleSided,
            databaseConfig: dbConfig
        };

        try {
            const savedDesign = await DesignStorageService.saveDesign(designData);
            setCurrentDesignId(savedDesign.id);
            setCurrentDesignName(savedDesign.name);
            setHasUnsavedChanges(false);
            showSuccess(`Diseño "${savedDesign.name}" guardado correctamente`);
        } catch (error) {
            console.error('Error saving design:', error);
            showError(`Error al guardar: ${error.message || 'Error desconocido'}`);
        }
    };

    const handleBackToDashboard = () => {
        if (hasUnsavedChanges) {
            if (window.confirm('Hay cambios no guardados. Presiona ACEPTAR para DESCARTARLOS y salir, o CANCELAR para quedarte y guardar.')) {
                navigate('/dashboard');
            }
        } else {
            navigate('/dashboard');
        }
    };

    const handleLoadDesign = (design) => {
        try {
            showInfo(`Cargando diseño: ${design.name}...`);

            // 1. Restaurar Estados Básicos
            setCurrentDesignId(design.id);
            setCurrentDesignName(design.name);

            if (design.canvasData) {
                setIsDoubleSided(!!design.canvasData.isDoubleSided);
                // setCurrentSide(design.canvasData.currentSide || 'front'); // Mejor forzar front al inicio
            }

            // 2. Restaurar Canvas Frontal
            if (design.canvasData?.front && frontFabricRef.current) {
                frontFabricRef.current.loadFromJSON(design.canvasData.front, () => {
                    frontFabricRef.current.renderAll();
                    console.log('Front canvas loaded');
                });
            }

            // 3. Restaurar Canvas Trasero (con delay para asegurar que el efecto de isDoubleSided cree el canvas)
            if (design.canvasData?.back && design.canvasData.isDoubleSided) {
                // Necesitamos esperar a que el efecto de isDoubleSided se ejecute y cree backFabricRef
                setTimeout(() => {
                    if (backFabricRef.current) {
                        backFabricRef.current.loadFromJSON(design.canvasData.back, () => {
                            backFabricRef.current.renderAll();
                            console.log('Back canvas loaded');
                        });
                    }
                }, 100);
            }

            // 4. Restaurar Configuración de Base de Datos
            if (design.databaseConfig) {
                if (design.databaseConfig.fieldConfigs) {
                    setFieldConfigs(design.databaseConfig.fieldConfigs);
                    // Actualizar localStorage también para persistencia
                    localStorage.setItem('matic_dataset_field_configs', JSON.stringify(design.databaseConfig.fieldConfigs));
                }

                if (design.databaseConfig.selectedDataset || design.databaseConfig.datasetName) {
                    const dsName = design.databaseConfig.selectedDataset || design.databaseConfig.datasetName;
                    setSelectedDataset(dsName);
                    // Esto disparará el useEffect que hace fetchRecords
                }
            }

            showSuccess('Diseño cargado correctamente');
            setShowOpenModal(false);

        } catch (error) {
            console.error('Error loading design:', error);
            showError('Error al cargar el diseño');
        }
    };

    const handleCardConfiguration = () => setShowCardConfigModal(true);
    const handleMeasurements = () => showInfo('Medidas: Próximamente');

    const handleAddText = () => {
        const text = new fabric.Textbox('Texto', {
            left: 100,
            top: 100,
            fontSize: 20,
            fontFamily: 'Arial',
            width: 150, // Ancho inicial para permitir wrapping
            splitByGrapheme: true
        });
        addObject(text);
    };

    const handleArtisticText = () => {
        setShowArtisticTextModal(true);
    };

    const handleGenerateArtisticText = (config) => {
        const textObj = new fabric.Text(config.text, {
            left: 100,
            top: 100,
            fontSize: config.fontSize,
            fontFamily: config.fontFamily,
            stroke: config.strokeColor,
            strokeWidth: config.strokeWidth,
            shadow: new fabric.Shadow({
                color: config.shadowColor,
                blur: config.shadowBlur,
                offsetX: config.shadowOffsetX,
                offsetY: config.shadowOffsetY
            })
        });

        if (config.fillType === 'gradient') {
            const gradient = new fabric.Gradient({
                type: 'linear',
                gradientUnits: 'percentage',
                coords: config.gradientDirection === 'horizontal' ? { x1: 0, y1: 0, x2: 1, y2: 0 } : { x1: 0, y1: 0, x2: 0, y2: 1 },
                colorStops: [
                    { offset: 0, color: config.gradientColor1 },
                    { offset: 1, color: config.gradientColor2 }
                ]
            });
            textObj.set('fill', gradient);
        } else {
            textObj.set('fill', config.fillColor);
        }

        addObject(textObj);
        showSuccess('Texto artístico añadido');
    };

    const handleCameraCapture = () => {
        setShowCameraModal(true);
    };

    const handleCaptureImage = (dataUrl) => {
        fabric.Image.fromURL(dataUrl, (img) => {
            img.set({
                left: 100,
                top: 100,
                scaleX: 0.5,
                scaleY: 0.5
            });
            addObject(img);
            showSuccess('Foto capturada añadida');
        });
    };

    const handleDrawSignature = () => {
        setShowSignatureModal(true);
    };

    const handleCaptureSignature = (dataUrl) => {
        fabric.Image.fromURL(dataUrl, (img) => {
            img.set({
                left: 100,
                top: 250,
                scaleX: 0.5,
                scaleY: 0.5
            });
            addObject(img);
            showSuccess('Firma añadida');
        });
    };

    const handleUploadSignature = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (f) => {
                    fabric.Image.fromURL(f.target.result, (img) => {
                        img.scaleToWidth(200);
                        img.set({ left: 100, top: 100 });
                        addObject(img);
                        showSuccess('Firma subida correctamente');
                    });
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    };

    const handleFingerprintCapture = () => {
        setShowFingerprintModal(true);
    };

    const handleCaptureFingerprint = (dataUrl) => {
        fabric.Image.fromURL(dataUrl, (img) => {
            img.set({
                left: 400,
                top: 100,
                scaleX: 0.5,
                scaleY: 0.5
            });
            addObject(img);
            showSuccess('Huella dactilar añadida');
        });
    };

    const handleAddRectangle = () => {
        const rect = new fabric.Rect({ left: 100, top: 100, fill: '#3b82f6', width: 100, height: 60 });
        addObject(rect);
    };

    const handleAddCircle = () => {
        const circle = new fabric.Circle({ left: 100, top: 100, fill: '#ef4444', radius: 40 });
        addObject(circle);
    };

    const handleAddLine = () => {
        const line = new fabric.Line([50, 50, 200, 50], { left: 100, top: 100, stroke: '#000000', strokeWidth: 2 });
        addObject(line);
    };

    const handleAddImage = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (f) => {
                    fabric.Image.fromURL(f.target.result, (img) => {
                        img.scaleToWidth(200);
                        addObject(img);
                    });
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    };

    const handleAddQrCode = () => {
        setShowCodeModal(true);
    };

    const handleAddBarcode1D = () => {
        setShowCodeModal(true);
    };

    const handleAddBarcode2D = () => {
        setShowCodeModal(true);
    };

    const handleGenerateCodeFromModal = async (type, data, codeType, codeFormat) => {
        try {
            if (type === 'qrCode') {
                // Generate QR
                let content = data;
                // Ensure proper formatting for special types if raw data passed
                if (codeType === 'email' && !data.startsWith('mailto:')) {
                    content = `mailto:${data} `;
                }
                // vCard and WiFi are usually fully formatted by the modal

                const qrDataUrl = await QRCode.toDataURL(content);
                fabric.Image.fromURL(qrDataUrl, (img) => {
                    img.set({
                        left: 100,
                        top: 100,
                        customType: 'qrCode',
                        codeType: codeType,
                        codeValue: data // Store raw data for editing
                    });
                    img.scaleToWidth(100);
                    addObject(img);
                    showSuccess('Código QR añadido');
                });
            } else if (type === 'barcode1d') {
                const canvas = document.createElement('canvas');
                JsBarcode(canvas, data, {
                    format: codeFormat,
                    displayValue: true
                });
                const barcodeDataUrl = canvas.toDataURL("image/png");
                fabric.Image.fromURL(barcodeDataUrl, (img) => {
                    img.set({
                        left: 100,
                        top: 100,
                        customType: 'barcode1d',
                        codeFormat: codeFormat,
                        codeValue: data
                    });
                    addObject(img);
                    showSuccess('Código de barras añadido');
                });
            }
        } catch (error) {
            console.error("Error generating code:", error);
            showError("Error al generar el código");
        }
    };





    const handleAddCounter = () => {
        const counter = new fabric.IText('0001', { left: 100, top: 100, fontSize: 24, fontFamily: 'Courier New', fill: '#d32f2f' });
        addObject(counter);
    };

    const handleAddDate = () => {
        const dateStr = new Date().toLocaleDateString();
        const dateText = new fabric.IText(dateStr, { left: 100, top: 100, fontSize: 20, fontFamily: 'Arial' });
        addObject(dateText);
    };

    // === MANEJADORES DE MODALES DE SEGURIDAD ===
    const handleAddNanoText = () => setShowNanoTextModal(true);
    const handleAddMicroText = () => setShowMicroTextModal(true);
    const handleAddUVLayer = () => setShowUVModal(true);
    const handleAddGhostImage = () => setShowGhostModal(true);
    const handleAddHologram = () => setShowHologramModal(true);

    // === GENERADORES DE ELEMENTOS DE SEGURIDAD ===
    const handleGenerateNanoText = (data) => {
        const text = new fabric.IText(data.text, {
            left: 100,
            top: 100,
            fontSize: data.fontSize,
            fontFamily: data.fontFamily,
            fill: data.fill,
            customType: 'nanoText',
            lockScalingX: true,
            lockScalingY: true,
            editable: true
        });
        addObject(text);
        showSuccess('Nano texto añadido');
    };

    const handleGenerateMicroText = (data) => {
        const text = new fabric.IText(data.text, {
            left: 100,
            top: 100,
            fontSize: data.fontSize,
            fontFamily: data.fontFamily,
            fill: data.fill,
            customType: 'microText'
        });
        addObject(text);
        showSuccess('Micro texto añadido');
    };

    const handleGenerateUVLayer = (data) => {
        // Si el modal generó una imagen (src existe), usarla
        if (data.src) {
            fabric.Image.fromURL(data.src, (img) => {
                img.set({
                    left: 100,
                    top: 100,
                    scaleX: 1,
                    scaleY: 1,
                    customType: 'uvLayer',
                    isSecurityFeature: true,
                    securityLevel: data.securityLevel || 'advanced',
                    effectType: data.effectType,
                    uvColor: data.uvColor,
                    intensity: data.intensity,
                    metadata: data.metadata || {}
                });

                addObject(img);
                showSuccess(`✅ Capa UV (${data.effectType}) añadida`);
            }, { crossOrigin: 'anonymous' });
        }
        // Lógica antigua para compatibilidad (si no hay src)
        else {
            const rect = new fabric.Rect({
                left: 100,
                top: 100,
                width: data.width,
                height: data.height,
                fill: 'rgba(255, 255, 0, ' + data.opacity + ')',
                stroke: data.color,
                strokeWidth: 1,
                strokeDashArray: data.pattern === 'dashed' ? [4, 4] : data.pattern === 'dots' ? [1, 3] : null,
                customType: 'uvLayer'
            });

            const label = new fabric.Text('UV LAYER', {
                fontSize: 12,
                fill: data.color,
                originX: 'center',
                originY: 'center',
                left: 100 + (data.width / 2),
                top: 100 + (data.height / 2)
            });

            const group = new fabric.Group([rect, label], {
                left: 100,
                top: 100,
                customType: 'uvLayerGroup'
            });

            addObject(group);
            showSuccess('Capa UV añadida');
        }
    };

    const handleGenerateGhostImage = (data) => {
        // Si el modal generó una imagen (src existe), usarla
        if (data.src) {
            fabric.Image.fromURL(data.src, (img) => {
                img.set({
                    left: 100,
                    top: 100,
                    scaleX: Math.min(1, 300 / img.width),
                    scaleY: Math.min(1, 300 / img.height),
                    customType: 'ghostImage',
                    isSecurityFeature: true,
                    securityLevel: data.securityLevel || 'advanced',
                    metadata: data.metadata || {}
                });

                addObject(img);
                showSuccess('✅ Imagen Fantasma (LetterScreen) añadida');
            }, { crossOrigin: 'anonymous' });
        }
        // Lógica antigua para compatibilidad (si no hay src)
        else if (data.sourceType === 'selection') {
            const activeObj = getActiveCanvas()?.getActiveObject();
            if (activeObj && activeObj.type === 'image') {
                activeObj.clone((cloned) => {
                    const filters = [];
                    if (data.filter === 'grayscale') filters.push(new fabric.Image.filters.Grayscale());
                    if (data.filter === 'sepia') filters.push(new fabric.Image.filters.Sepia());

                    cloned.set({
                        left: activeObj.left + 20,
                        top: activeObj.top + 20,
                        opacity: data.opacity,
                        filters: filters
                    });
                    cloned.applyFilters();
                    addObject(cloned);
                    showSuccess('Imagen fantasma generada');
                });
            } else {
                showError('No hay imagen seleccionada para generar fantasma');
            }
        } else {
            const circle = new fabric.Circle({
                left: 150,
                top: 150,
                radius: 40,
                fill: `rgba(200, 200, 200, ${data.opacity})`,
                stroke: '#aaaaaa',
                strokeWidth: 1,
                customType: 'ghostPlaceholder'
            });
            addObject(circle);
            showSuccess('Marcador fantasma añadido');
        }
    };

    const handleGenerateHologram = (data) => {
        const rect = new fabric.Rect({
            left: 100,
            top: 100,
            width: 100,
            height: 100,
            objectCaching: false
        });

        let gradient;
        if (data.hologramType === 'rainbow') {
            gradient = new fabric.Gradient({
                type: 'linear',
                gradientUnits: 'percentage',
                coords: { x1: 0, y1: 0, x2: 1, y2: 1 },
                colorStops: [
                    { offset: 0, color: `rgba(255,0,0,${data.opacity})` },
                    { offset: 0.2, color: `rgba(255,255,0,${data.opacity})` },
                    { offset: 0.4, color: `rgba(0,255,0,${data.opacity})` },
                    { offset: 0.6, color: `rgba(0,255,255,${data.opacity})` },
                    { offset: 0.8, color: `rgba(0,0,255,${data.opacity})` },
                    { offset: 1, color: `rgba(255,0,255,${data.opacity})` }
                ]
            });
        } else if (data.hologramType === 'globe') {
            gradient = new fabric.Gradient({
                type: 'radial',
                gradientUnits: 'percentage',
                coords: { x1: 0.5, y1: 0.5, r1: 0, x2: 0.5, y2: 0.5, r2: 0.5 },
                colorStops: [
                    { offset: 0, color: `rgba(0,0,255,${data.opacity})` },
                    { offset: 1, color: `rgba(0,0,0,0)` }
                ]
            });
        } else {
            gradient = new fabric.Gradient({
                type: 'linear',
                gradientUnits: 'percentage',
                coords: { x1: 0, y1: 0, x2: 1, y2: 0 },
                colorStops: [
                    { offset: 0, color: `rgba(200,200,200,${data.opacity})` },
                    { offset: 0.5, color: `rgba(255,255,255,${data.opacity})` },
                    { offset: 1, color: `rgba(200,200,200,${data.opacity})` }
                ]
            });
        }

        rect.set('fill', gradient);
        rect.set({
            customType: 'hologram',
            stroke: `rgba(255,255,255,${data.opacity})`,
            strokeWidth: 1,
            angle: data.angle
        });

        addObject(rect);
        showSuccess('Holograma añadido');
    };

    const handleZoomIn = () => {
        const canvas = getActiveCanvas();
        if (canvas) {
            let zoom = canvas.getZoom();
            zoom = Math.min(zoom * 1.1, 5);
            canvas.setZoom(zoom);
            setZoomLevel(zoom);
            showInfo(`Zoom: ${Math.round(zoom * 100)}% `);
        }
    };

    const handleZoomOut = () => {
        const canvas = getActiveCanvas();
        if (canvas) {
            let zoom = canvas.getZoom();
            zoom = Math.max(zoom / 1.1, 0.5);
            canvas.setZoom(zoom);
            setZoomLevel(zoom);
            showInfo(`Zoom: ${Math.round(zoom * 100)}% `);
        }
    };

    const handleFitToWindow = () => {
        const canvas = getActiveCanvas();
        if (canvas) {
            canvas.setZoom(1);
            canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
            setZoomLevel(1);
            showSuccess('Vista restablecida');
        }
    };

    const handleToggleGrid = () => {
        const canvas = getActiveCanvas();
        if (!canvas) return;
        const existingGrid = canvas.getObjects().filter(o => o.isGrid);
        if (existingGrid.length > 0) {
            existingGrid.forEach(o => canvas.remove(o));
            showInfo('Cuadrícula oculta');
        } else {
            const gridSize = 20;
            for (let i = 0; i < (canvas.width / gridSize); i++) {
                canvas.add(new fabric.Line([i * gridSize, 0, i * gridSize, canvas.height], { stroke: '#ccc', selectable: false, isGrid: true }));
                canvas.add(new fabric.Line([0, i * gridSize, canvas.width, i * gridSize], { stroke: '#ccc', selectable: false, isGrid: true }));
            }
            showInfo('Cuadrícula mostrada');
        }
    };

    const handleToggleRuler = () => {
        const canvas = getActiveCanvas();
        if (!canvas) return;
        const existingRuler = canvas.getObjects().filter(o => o.isRuler);
        if (existingRuler.length > 0) {
            existingRuler.forEach(o => canvas.remove(o));
            showInfo('Reglas ocultas');
        } else {
            const rulerColor = '#999';
            // Top ruler
            canvas.add(new fabric.Rect({ left: 0, top: 0, width: canvas.width, height: 20, fill: '#f0f0f0', selectable: false, isRuler: true }));
            for (let i = 0; i < canvas.width; i += 50) {
                canvas.add(new fabric.Line([i, 0, i, 20], { stroke: rulerColor, selectable: false, isRuler: true }));
                canvas.add(new fabric.Text(i.toString(), { left: i + 2, top: 5, fontSize: 10, selectable: false, isRuler: true }));
            }
            // Left ruler
            canvas.add(new fabric.Rect({ left: 0, top: 0, width: 20, height: canvas.height, fill: '#f0f0f0', selectable: false, isRuler: true }));
            for (let i = 0; i < canvas.height; i += 50) {
                canvas.add(new fabric.Line([0, i, 20, i], { stroke: rulerColor, selectable: false, isRuler: true }));
                canvas.add(new fabric.Text(i.toString(), { left: 2, top: i + 5, fontSize: 10, angle: -90, selectable: false, isRuler: true }));
            }
            showInfo('Reglas mostradas');
        }
    };

    const handleToggleSmartGuides = () => {
        showSuccess('Guías inteligentes activadas');
    };

    const handleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    const handlePreview = () => {
        handlePrintPreview();
    };

    // Manejador de Vista Previa
    const handlePrintPreview = () => {
        const frontCanvas = frontFabricRef.current;
        if (!frontCanvas) return;

        frontCanvas.discardActiveObject();
        frontCanvas.renderAll();

        const frontData = frontCanvas.toDataURL({
            format: 'png',
            quality: 1,
            multiplier: 2
        });
        setPreviewImage(frontData);

        if (isDoubleSided && backFabricRef.current) {
            const backCanvas = backFabricRef.current;
            backCanvas.discardActiveObject();
            backCanvas.renderAll();
            const backData = backCanvas.toDataURL({
                format: 'png',
                quality: 1,
                multiplier: 2
            });
            setPreviewBackImage(backData);
        } else {
            setPreviewBackImage(null);
        }

        setShowPrintPreview(true);
    };

    const handlePrint = async () => {
        if (!selectedPrinter) {
            showError('Por favor selecciona una impresora');
            return;
        }

        setIsPrinting(true);
        try {
            const printerObj = printers.find(p => p._id === selectedPrinter);
            if (!printerObj) {
                throw new Error('Selecciona una impresora válida');
            }
            const printerName = printerObj.Name || printerObj.Model;
            const baseUrl = getApiBaseUrl();

            // Determinar rango de impresión
            let startIndex = currentRecordIndex;
            let endIndex = currentRecordIndex;

            if (selectedDataset && datasetRecords.length > 0) {
                if (printMode === 'all') {
                    startIndex = 0;
                    endIndex = datasetRecords.length - 1;
                } else if (printMode === 'range') {
                    startIndex = Math.max(0, printRangeStart - 1);
                    endIndex = Math.min(datasetRecords.length - 1, printRangeEnd - 1);
                }
            }

            if (startIndex > endIndex) {
                throw new Error('Rango de impresión inválido');
            }

            const totalToPrint = endIndex - startIndex + 1;
            if (totalToPrint > 1) {
                showInfo(`Iniciando impresión de ${totalToPrint} registro(s)...`);
            }

            // Loop de impresión
            for (let i = startIndex; i <= endIndex; i++) {
                // Si es impresión múltiple, actualizar datos
                if (selectedDataset && datasetRecords.length > 0 && totalToPrint > 1) {
                    showInfo(`Procesando registro ${i + 1} de ${datasetRecords.length}...`);
                    applyDataToCanvas(i);
                    // Esperar brevemente para carga de imágenes (hack temporal hasta refactorizar applyDataToCanvas)
                    await new Promise(resolve => setTimeout(resolve, 800));
                }

                // 1. Verificar conexión (opcional, se hace en el backend también)
                const connectRes = await fetch(`${baseUrl}/api/printer/connect`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ printer_name: printerName })
                });

                if (!connectRes.ok) {
                    throw new Error('No se pudo conectar con la impresora');
                }

                // 2. Preparar datos de impresión
                const frontCanvas = frontFabricRef.current;
                frontCanvas.discardActiveObject();
                frontCanvas.renderAll();
                const frontData = frontCanvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });

                let backData = null;
                if (isDoubleSided && backFabricRef.current) {
                    const backCanvas = backFabricRef.current;
                    backCanvas.discardActiveObject();
                    backCanvas.renderAll();
                    backData = backCanvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });
                }

                // Detectar tipo de cinta
                let ribbonType = 'YMCKO'; // Default
                if (printerObj.ribbonType) {
                    ribbonType = printerObj.ribbonType;
                } else if (printerObj.status && printerObj.status.includes('YMCKOK')) {
                    ribbonType = 'YMCKOK';
                } else if (printerObj.status && (printerObj.status.includes('Black') || printerObj.status.includes('Monochrome'))) {
                    ribbonType = 'Monochrome';
                }

                let frontMode = 'color';
                let backMode = null;

                if (ribbonType === 'Monochrome') {
                    frontMode = 'mono';
                    if (isDoubleSided) backMode = 'mono';
                } else if (ribbonType === 'YMCKOK') {
                    frontMode = 'color';
                    if (isDoubleSided) backMode = 'mono';
                } else {
                    frontMode = 'color';
                    if (isDoubleSided) backMode = 'color';
                }

                const orientation = frontCanvas.width > frontCanvas.height ? 'landscape' : 'portrait';

                // 3. Enviar trabajo de impresión
                showInfo(`Enviando trabajo (${ribbonType})...`);
                const printRes = await fetch(`${baseUrl}/api/printer/print`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        printer_name: printerName,
                        user_id: localStorage.getItem('accountId'),
                        front_image: frontData,
                        back_image: backData,
                        options: {
                            orientation: orientation,
                            copies: 1,
                            double_sided: isDoubleSided,
                            ribbon_type: ribbonType,
                            front_mode: frontMode,
                            back_mode: backMode
                        }
                    })
                });

                const printResult = await printRes.json();

                if (!printResult.success) {
                    throw new Error(printResult.message || `Error al imprimir registro ${i + 1}`);
                }

                // Pequeña pausa entre trabajos para no saturar
                if (totalToPrint > 1) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }

            showSuccess('🖨️ Trabajo de impresión completado');
            setShowPrintPreview(false);

        } catch (error) {
            console.error('Print Error:', error);
            showError(error.message || 'Error de comunicación con la impresora');
        } finally {
            setIsPrinting(false);
        }
    };

    const handleDatasetSelection = (dataset) => {
        console.log('Dataset seleccionado:', dataset);
        setSelectedDataset(dataset.dataset_name);
        setShowDatabaseManager(false);
        fetchDatasets();
    };

    return (
        <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden font-sans">
            {/* Sidebar Izquierdo */}
            <div className="w-72 bg-slate-900 flex-none z-30 shadow-2xl flex flex-col border-r border-slate-800">
                <div className="p-4 bg-slate-950 border-b border-slate-800">
                    <button
                        onClick={handleBackToDashboard}
                        className="mb-3 flex items-center text-slate-400 hover:text-white transition-colors text-sm font-medium"
                    >
                        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Volver al Dashboard
                    </button>
                    <h1 className="text-white font-bold text-lg tracking-tight">Diseñador de Tarjetas</h1>
                    <p className="text-slate-400 text-xs">Versión Profesional</p>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <AccordionToolbar
                        onNewDocument={handleNewDocument}
                        onTemplateSelection={handleTemplateSelection}
                        onSave={handleSaveDesign}
                        onExport={handleExportAdvanced}
                        onExportAdvanced={handleExportAdvanced}
                        onAddText={handleAddText}
                        onArtisticText={handleArtisticText}
                        onAddRectangle={handleAddRectangle}
                        onAddCircle={handleAddCircle}
                        onAddLine={handleAddLine}
                        onAddImage={handleAddImage}
                        onAddQrCode={handleAddQrCode}
                        onAddBarcode1D={handleAddBarcode1D}
                        onAddBarcode2D={handleAddBarcode2D}
                        onAddCounter={handleAddCounter}
                        onAddDate={handleAddDate}
                        onAddNanoText={handleAddNanoText}
                        onAddMicroText={handleAddMicroText}
                        onAddUVLayer={handleAddUVLayer}
                        onAddGhostImage={handleAddGhostImage}
                        onAddHologram={handleAddHologram}
                        onCameraCapture={handleCameraCapture}
                        onDrawSignature={handleDrawSignature}
                        onUploadSignature={handleUploadSignature}
                        onFingerprintCapture={() => showInfo('Huella dactilar lista')}
                        onZoomIn={handleZoomIn}
                        onZoomOut={handleZoomOut}
                        onFitToWindow={handleFitToWindow}
                        onFullscreen={handleFullscreen}
                        onPreview={handlePreview}
                        onToggleGrid={handleToggleGrid}
                        onToggleRuler={handleToggleRuler}
                        onToggleSmartGuides={handleToggleSmartGuides}
                        onCardConfiguration={handleCardConfiguration}
                        onMeasurements={handleMeasurements}
                    />
                </div>
            </div>

            {/* Área Central */}
            <div className="flex-1 flex flex-col relative overflow-hidden bg-slate-50">
                <div className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6 shadow-sm z-10">
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2 bg-slate-100 px-3 py-1 rounded-full">
                            <span className={`w - 2 h - 2 rounded - full ${isDoubleSided ? 'bg-blue-500' : 'bg-green-500'} animate - pulse`}></span>
                            <span className="text-sm font-medium text-slate-600">
                                {isDoubleSided ? `Editando: ${currentSide === 'front' ? 'Frente' : 'Reverso'} ` : 'Editando: Frente (Modo Simple)'}
                            </span>
                        </div>

                        <button
                            onClick={() => setIsDoubleSided(!isDoubleSided)}
                            className={`px - 3 py - 1 rounded - md border text - xs font - medium transition - all ${isDoubleSided ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'} `}
                            title={isDoubleSided ? "Cambiar a impresión de una sola cara" : "Activar impresión a doble cara"}
                        >
                            {isDoubleSided ? '🔄 Doble Cara Activo' : '📄 Una Cara'}
                        </button>
                    </div>

                    {isDoubleSided && (
                        <div className="flex bg-slate-100 rounded-lg p-1">
                            <button
                                onClick={() => setCurrentSide('front')}
                                className={`px - 4 py - 1.5 rounded - md text - sm font - medium transition - all ${currentSide === 'front' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'} `}
                            >
                                Frente
                            </button>
                            <button
                                onClick={() => setCurrentSide('back')}
                                className={`px - 4 py - 1.5 rounded - md text - sm font - medium transition - all ${currentSide === 'back' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'} `}
                            >
                                Reverso
                            </button>
                        </div>
                    )}

                    <div className="flex items-center space-x-3">
                        <div className="flex items-center bg-slate-50 border border-slate-200 rounded-md px-2 py-1">
                            <span className="text-xs text-slate-400 mr-2">Impresora:</span>
                            <select
                                value={selectedPrinter}
                                onChange={(e) => setSelectedPrinter(e.target.value)}
                                className="bg-transparent text-sm font-medium text-slate-700 focus:outline-none cursor-pointer min-w-[120px]"
                            >
                                {printers.length === 0 && <option value="">Buscando...</option>}
                                {printers.map(p => (
                                    <option key={p._id} value={p._id}>{p.Name || p.Model}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={handlePrintPreview}
                            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-md text-sm font-medium shadow-sm transition-all"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            <span>Imprimir</span>
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto flex items-center justify-center p-12 bg-slate-50 relative">
                    <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>

                    <div className="relative z-0 transition-transform duration-200 ease-out">
                        <div
                            className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
                            style={{
                                display: currentSide === 'front' ? 'block' : 'none',
                                width: canvasDimensions.width,
                                height: canvasDimensions.height,
                                transition: 'width 0.3s, height 0.3s'
                            }}
                        >
                            <canvas ref={frontCanvasEl} />
                        </div>
                        <div
                            className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
                            style={{
                                display: currentSide === 'back' ? 'block' : 'none',
                                width: canvasDimensions.width,
                                height: canvasDimensions.height,
                                transition: 'width 0.3s, height 0.3s'
                            }}
                        >
                            <canvas ref={backCanvasEl} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Sidebar Derecho (Propiedades) - TEMA OSCURO */}
            <div className="w-80 bg-slate-900 border-l border-slate-800 flex-none overflow-y-auto z-20 shadow-lg flex flex-col text-slate-200">
                <div className="p-4 border-b border-slate-800 bg-slate-950">
                    <h2 className="font-semibold text-white">Propiedades</h2>
                </div>
                <div className="flex-1 overflow-y-auto">
                    <PropertiesPanel
                        getActiveCanvas={getActiveCanvas}
                        selectedObject={selectedObject}
                        onUpdateCode={handleUpdateCode}
                        onUpdateProperty={handleUpdateProperty}
                        datasets={datasets}
                        selectedDataset={selectedDataset}
                        onSelectDataset={setSelectedDataset}
                        datasetFields={datasetFields}
                        currentRecord={datasetRecords[currentRecordIndex]}
                        onDatabaseOpen={handleDatabaseOpen}

                        onRefreshDatasets={fetchDatasets}
                        updateTrigger={propertyUpdateTrigger}
                        fieldConfigs={fieldConfigs}
                    />
                </div>
            </div>

            {/* Modal de Vista Previa */}
            {showPrintPreview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-lg text-slate-800">Vista Previa de Impresión</h3>
                            <button onClick={() => setShowPrintPreview(false)} className="text-slate-400 hover:text-slate-600">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="p-8 flex flex-col items-center bg-slate-100 max-h-[70vh] overflow-y-auto">
                            <div className="flex flex-wrap justify-center gap-8">
                                {/* Frente */}
                                <div className="flex flex-col items-center">
                                    <span className="mb-2 text-sm font-bold text-slate-500 uppercase tracking-wider">Frente</span>
                                    <div className="bg-white p-2 shadow-lg rounded-lg">
                                        <img src={previewImage} alt="Vista Previa Frente" className="max-w-[400px] h-auto rounded border border-slate-200" />
                                    </div>
                                </div>

                                {/* Reverso (si existe) */}
                                {previewBackImage && (
                                    <div className="flex flex-col items-center">
                                        <span className="mb-2 text-sm font-bold text-slate-500 uppercase tracking-wider">Reverso</span>
                                        <div className="bg-white p-2 shadow-lg rounded-lg">
                                            <img src={previewBackImage} alt="Vista Previa Reverso" className="max-w-[400px] h-auto rounded border border-slate-200" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <p className="mt-8 text-sm text-slate-500">Impresora seleccionada: <span className="font-medium text-slate-800">{printers.find(p => p._id === selectedPrinter)?.Name || 'Desconocida'}</span></p>

                            {selectedDataset && datasetRecords.length > 0 && (
                                <div className="mt-6 w-full max-w-md bg-white p-4 rounded-lg shadow-sm border border-slate-200">
                                    <h4 className="font-bold text-slate-700 mb-3 text-sm uppercase">Opciones de Impresión por Lotes</h4>
                                    <div className="space-y-3">
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="printMode"
                                                value="current"
                                                checked={printMode === 'current'}
                                                onChange={(e) => setPrintMode(e.target.value)}
                                                className="text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-slate-700">Registro Actual ({currentRecordIndex + 1})</span>
                                        </label>

                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="printMode"
                                                value="all"
                                                checked={printMode === 'all'}
                                                onChange={(e) => setPrintMode(e.target.value)}
                                                className="text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-slate-700">Todos los Registros ({datasetRecords.length})</span>
                                        </label>

                                        <div className="flex items-center space-x-2">
                                            <label className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="printMode"
                                                    value="range"
                                                    checked={printMode === 'range'}
                                                    onChange={(e) => setPrintMode(e.target.value)}
                                                    className="text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="text-sm text-slate-700">Rango:</span>
                                            </label>
                                            <div className="flex items-center space-x-2 ml-4">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={datasetRecords.length}
                                                    value={printRangeStart}
                                                    onChange={(e) => setPrintRangeStart(parseInt(e.target.value) || 1)}
                                                    disabled={printMode !== 'range'}
                                                    className="w-16 px-2 py-1 border border-slate-300 rounded text-sm disabled:bg-slate-100"
                                                />
                                                <span className="text-slate-400">-</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={datasetRecords.length}
                                                    value={printRangeEnd}
                                                    onChange={(e) => setPrintRangeEnd(parseInt(e.target.value) || 1)}
                                                    disabled={printMode !== 'range'}
                                                    className="w-16 px-2 py-1 border border-slate-300 rounded text-sm disabled:bg-slate-100"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="p-4 border-t border-slate-100 flex justify-end space-x-3 bg-white">
                            <button
                                onClick={() => setShowPrintPreview(false)}
                                className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 font-medium transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handlePrint}
                                disabled={isPrinting}
                                className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-md transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isPrinting ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        <span>Enviando...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                        <span>Confirmar Impresión</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Barra de Navegación de Datos (Solo visible si hay un dataset seleccionado) */}
            {selectedDataset && datasetRecords.length > 0 && (
                <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-4 z-50 border border-slate-600">
                    <button
                        onClick={handlePrevRecord}
                        disabled={currentRecordIndex === 0}
                        className="hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        ◀
                    </button>
                    <span className="text-sm font-medium">
                        Registro {currentRecordIndex + 1} / {datasetRecords.length}
                    </span>
                    <button
                        onClick={handleNextRecord}
                        disabled={currentRecordIndex === datasetRecords.length - 1}
                        className="hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        ▶
                    </button>
                </div>
            )}

            {/* Modal de Database Manager */}
            {showDatabaseManager && (
                <DatabaseManager
                    userId={localStorage.getItem('accountId') || user?._id || user?.accountId}
                    onClose={() => setShowDatabaseManager(false)}
                    onSelect={handleDatasetSelection}
                />
            )}

            {/* Modal de Generador de Códigos */}
            <CodeGeneratorModal
                isOpen={showCodeModal}
                onClose={() => setShowCodeModal(false)}
                onGenerate={handleGenerateCodeFromModal}
            />

            {/* Modales de Seguridad Avanzada */}
            <NanoTextModal
                isOpen={showNanoTextModal}
                onClose={() => setShowNanoTextModal(false)}
                onGenerate={handleGenerateNanoText}
            />
            <MicroTextModal
                isOpen={showMicroTextModal}
                onClose={() => setShowMicroTextModal(false)}
                onGenerate={handleGenerateMicroText}
            />
            <UVLayerModal
                isOpen={showUVModal}
                onClose={() => setShowUVModal(false)}
                onGenerate={handleGenerateUVLayer}
            />
            <GhostImageModal
                isOpen={showGhostModal}
                onClose={() => setShowGhostModal(false)}
                onGenerate={handleGenerateGhostImage}
                hasSelectedImage={selectedObject && selectedObject.type === 'image'}
            />
            <HologramModal
                isOpen={showHologramModal}
                onClose={() => setShowHologramModal(false)}
                onGenerate={handleGenerateHologram}
            />
            <CardConfigModal
                isOpen={showCardConfigModal}
                onClose={() => setShowCardConfigModal(false)}
                showSuccess={showSuccess}
                showWarning={showWarning}
                showError={showError}
                frontCanvas={frontFabricRef.current}
                backCanvas={backFabricRef.current}
                currentSide={currentSide}
                onUpdateDimensions={setCanvasDimensions}
            />
            <TemplateModal
                isOpen={showTemplateModal}
                onClose={(objects) => {
                    if (!objects) {
                        setShowTemplateModal(false);
                        return;
                    }

                    const canvas = getActiveCanvas();
                    if (!canvas) return;

                    if (confirm('¿Aplicar plantilla? Se reemplazará el diseño actual.')) {
                        canvas.clear();
                        canvas.setBackgroundColor('#ffffff', canvas.renderAll.bind(canvas));

                        objects.forEach(objData => {
                            let obj;
                            if (objData.type === 'rect') {
                                obj = new fabric.Rect(objData);
                            } else if (objData.type === 'circle') {
                                obj = new fabric.Circle(objData);
                            } else if (objData.type === 'text') {
                                obj = new fabric.Text(objData.text, objData);
                            }

                            if (obj) {
                                canvas.add(obj);
                            }
                        });

                        canvas.renderAll();
                        setShowTemplateModal(false);
                    }
                }}
                showSuccess={showSuccess}
            />
            <ArtisticTextModal
                isOpen={showArtisticTextModal}
                onClose={() => setShowArtisticTextModal(false)}
                onGenerate={handleGenerateArtisticText}
            />
            <CameraCaptureModal
                isOpen={showCameraModal}
                onClose={() => setShowCameraModal(false)}
                onCapture={handleCaptureImage}
            />
            <SignatureCaptureModal
                isOpen={showSignatureModal}
                onClose={() => setShowSignatureModal(false)}
                onCapture={handleCaptureSignature}
            />
            <FingerprintCaptureModal
                isOpen={showFingerprintModal}
                onClose={() => setShowFingerprintModal(false)}
                onCapture={handleCaptureFingerprint}
            />
            <DesignBrowserModal
                isOpen={showOpenModal}
                onClose={() => setShowOpenModal(false)}
                onSelect={handleLoadDesign}
            />
            <SaveDesignModal
                isOpen={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                onSave={executeSave}
                currentName={currentDesignName}
            />
            <ExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExport={executeExport}
            />
        </div>
    );
});

export default CardDesigner;
